/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened, and lets
 * shoppers browse between fullscreen images (arrows, arrow keys, swipe).
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog[data-zoom-dialog]          — one fullscreen dialog per image, in gallery order.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 *   dialog [data-zoom-step="-1|1"]    — previous / next button inside a dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleStepClick, opts);
    this.addEventListener('keydown', this.#handleKeydown, opts);
    this.addEventListener('pointerdown', this.#handlePointerDown, opts);
    this.addEventListener('pointerup', this.#handlePointerUp, opts);
    this.addEventListener('pointercancel', () => { this.#swipe = null; }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };


  /* Fullscreen browsing: arrow buttons, ArrowLeft/ArrowRight keys and horizontal swipe. */
  #step(fromDialog, delta) {
    const dialogs = [...this.querySelectorAll('dialog[data-zoom-dialog]')];
    if (dialogs.length < 2) return;
    const index = dialogs.indexOf(fromDialog);
    if (index === -1) return;
    const target = dialogs[(index + delta + dialogs.length) % dialogs.length];
    const image = target.querySelector('img[data-zoom-image]');
    if (image instanceof HTMLImageElement) image.loading = 'eager';
    fromDialog.close();
    target.showModal();
    const sameButton = target.querySelector(`[data-zoom-step="${delta < 0 ? -1 : 1}"]`);
    if (sameButton instanceof HTMLElement) sameButton.focus();
  }

  #rtlDelta(dialog, delta) {
    return getComputedStyle(dialog).direction === 'rtl' ? -delta : delta;
  }

  #handleStepClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-zoom-step]');
    if (!button || !this.contains(button)) return;
    const dialog = button.closest('dialog[data-zoom-dialog]');
    if (!dialog) return;
    event.stopPropagation();
    this.#step(dialog, Number(button.getAttribute('data-zoom-step')));
  };

  #handleKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!(event.target instanceof Element)) return;
    const dialog = event.target.closest('dialog[data-zoom-dialog][open]');
    if (!dialog || !this.contains(dialog)) return;
    event.preventDefault();
    this.#step(dialog, this.#rtlDelta(dialog, event.key === 'ArrowLeft' ? -1 : 1));
  };

  #swipe = null;

  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    if (!(event.target instanceof Element)) return;
    const dialog = event.target.closest('dialog[data-zoom-dialog][open]');
    if (!dialog) return;
    this.#swipe = { dialog, x: event.clientX, y: event.clientY };
  };

  #handlePointerUp = (event) => {
    const swipe = this.#swipe;
    this.#swipe = null;
    if (!swipe) return;
    const dx = event.clientX - swipe.x;
    const dy = event.clientY - swipe.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    this.#step(swipe.dialog, this.#rtlDelta(swipe.dialog, dx < 0 ? 1 : -1));
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
